/**
 * @module components/LabelContent
 * @description 数据标签内容多选（metric / dimension / percent / total）
 *
 * 读: useSettingObjectState('labelContent')
 * 写: setValue；可选项由当前 chartType + QB 维指数量实时计算
 */

import React, { useMemo, useCallback } from 'react';
import { useIntl } from 'umi';
import TagFilterSelect from '@/pages/datalab/components/TagFilterSelect';
import { useSettingObjectState } from '../../../context';
import { useQuerySelector } from '../../../hooks/deps';
import { toChartTypeEnum } from '../../../utils/toChartTypeEnum';
import { EMPTY_COLUMN_INFO_LIST } from '../../../utils/constants';
import { getLabelContentOptions } from './getLabelContentOptions';
import './index.scss';

const LabelContent: React.FC = () => {
  const { formatMessage, locale } = useIntl();
  const { value, setValue } = useSettingObjectState('labelContent');
  const chartTypeStr = useQuerySelector(s => s.chartType as string);
  const columnDimensions = useQuerySelector(
    s => s.columnDimensions ?? EMPTY_COLUMN_INFO_LIST,
  );
  const columnMetrics = useQuerySelector(
    s => s.columnMetrics ?? EMPTY_COLUMN_INFO_LIST,
  );
  const chartType = useMemo(
    () => toChartTypeEnum(chartTypeStr),
    [chartTypeStr],
  );

  const handleChange = useCallback(
    (newValue: string[]) => {
      setValue(newValue);
    },
    [setValue],
  );

  const options = useMemo(
    () => getLabelContentOptions({
      chartType,
      dimensionCount: columnDimensions.length,
      metricCount: columnMetrics.length,
      formatMessage,
    }),
    [chartType, columnDimensions.length, columnMetrics.length, formatMessage],
  );

  return (
    <TagFilterSelect
      className="dbl-chart-label-content-select"
      overlayClassName="dbl-chart-label-content-select-overlay"
      value={value}
      locale={locale}
      onChange={handleChange}
      options={options}
      name={formatMessage({ id: 'dbl_chart_label_content' })}
      placeholder={formatMessage({ id: 'dbl_chart_label_content_placeholder' })}
      listWidth={200}
      listHeight={180}
      searchable={false}
      tooltip={null}
    />
  );
};

export default React.memo(LabelContent);

